import { useAuth, getSafeReturnTo } from './chunk-LUC3HWI7.js';
import { Brand } from './chunk-EHMYFYGQ.js';
import { defaultLinkComponent } from './chunk-46OQMWYR.js';
import { useT, AdminerContext } from './chunk-3G2YSMCN.js';
import './chunk-4DPTK2CR.js';
import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import clsx2 from 'clsx';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { NavLink, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { Menu } from 'lucide-react';

var ShellContext = createContext(null);
function useShellDrawer() {
  return useContext(ShellContext);
}
function AppShell({ sidebar, children, toaster, className }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lastFocusRef = useRef(null);
  const openDrawer = useCallback(() => {
    lastFocusRef.current = document.activeElement;
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => lastFocusRef.current?.focus(), 220);
  }, []);
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e) {
      if (e.key === "Escape") closeDrawer();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);
  return /* @__PURE__ */ jsx(ShellContext.Provider, { value: { drawerOpen, openDrawer, closeDrawer }, children: /* @__PURE__ */ jsxs("div", { className: clsx2("adminer-AppShell", className), children: [
    drawerOpen && /* @__PURE__ */ jsx(
      "div",
      {
        className: "adminer-AppShell__backdrop",
        onClick: closeDrawer,
        "aria-hidden": "true"
      }
    ),
    sidebar,
    /* @__PURE__ */ jsx("main", { className: "adminer-AppShell__main", children }),
    toaster
  ] }) });
}
function defaultInitials(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function Sidebar({
  groups,
  user,
  profileHref,
  onLogout,
  logoutIcon,
  brand,
  className
}) {
  const t = useT();
  const drawer = useShellDrawer();
  const open = drawer?.drawerOpen ?? false;
  useEffect(() => {
    if (!open || !drawer) return;
    function onClickLink(e) {
      const target = e.target;
      if (target?.closest(".adminer-Sidebar__link")) drawer.closeDrawer();
    }
    document.addEventListener("click", onClickLink, true);
    return () => document.removeEventListener("click", onClickLink, true);
  }, [open, drawer]);
  return /* @__PURE__ */ jsxs(
    "aside",
    {
      className: clsx2(
        "adminer-Sidebar",
        open && "adminer-Sidebar--open",
        className
      ),
      "aria-label": t("sidebar.navAria"),
      children: [
        brand !== null && /* @__PURE__ */ jsx("div", { className: "adminer-Sidebar__brand", children: brand ?? /* @__PURE__ */ jsx(Brand, {}) }),
        /* @__PURE__ */ jsx("nav", { className: "adminer-Sidebar__nav", children: groups.map((group, gi) => /* @__PURE__ */ jsxs("div", { className: "adminer-Sidebar__group", children: [
          group.label !== "" && group.label !== null && /* @__PURE__ */ jsx("div", { className: "adminer-Sidebar__groupLabel", children: group.label }),
          group.items.map((item) => /* @__PURE__ */ jsxs(
            NavLink,
            {
              to: item.to,
              end: item.end,
              className: ({ isActive }) => clsx2(
                "adminer-Sidebar__link",
                isActive && "adminer-Sidebar__link--active"
              ),
              children: [
                item.icon && /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__icon", "aria-hidden": "true", children: item.icon }),
                /* @__PURE__ */ jsx("span", { children: item.label })
              ]
            },
            item.to
          ))
        ] }, gi)) }),
        /* @__PURE__ */ jsxs("div", { className: "adminer-Sidebar__footer", children: [
          profileHref ? /* @__PURE__ */ jsxs(
            NavLink,
            {
              to: profileHref,
              className: ({ isActive }) => clsx2(
                "adminer-Sidebar__profile",
                isActive && "adminer-Sidebar__profile--active"
              ),
              "aria-label": t("sidebar.profileAria"),
              children: [
                /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__avatar", "aria-hidden": "true", children: user.initials ?? defaultInitials(user.displayName) }),
                /* @__PURE__ */ jsxs("span", { className: "adminer-Sidebar__profileText", children: [
                  /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__profileName", children: user.displayName }),
                  user.roleLabel && /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__profileRole", children: user.roleLabel })
                ] })
              ]
            }
          ) : /* @__PURE__ */ jsxs("div", { className: "adminer-Sidebar__profile", children: [
            /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__avatar", "aria-hidden": "true", children: user.initials ?? defaultInitials(user.displayName) }),
            /* @__PURE__ */ jsxs("span", { className: "adminer-Sidebar__profileText", children: [
              /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__profileName", children: user.displayName }),
              user.roleLabel && /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__profileRole", children: user.roleLabel })
            ] })
          ] }),
          onLogout && /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: "adminer-Sidebar__logout",
              onClick: onLogout,
              "aria-label": t("sidebar.logoutAria"),
              children: [
                logoutIcon && /* @__PURE__ */ jsx("span", { className: "adminer-Sidebar__logoutIcon", "aria-hidden": "true", children: logoutIcon }),
                /* @__PURE__ */ jsx("span", { children: t("sidebar.logout") })
              ]
            }
          )
        ] })
      ]
    }
  );
}
function Topbar({ crumbs, right, className }) {
  const t = useT();
  const drawer = useShellDrawer();
  const ctx = useContext(AdminerContext);
  const Link = ctx?.LinkComponent ?? defaultLinkComponent;
  return /* @__PURE__ */ jsxs("header", { className: ["adminer-Topbar", className].filter(Boolean).join(" "), children: [
    drawer && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "adminer-Topbar__hamburger",
        onClick: drawer.openDrawer,
        "aria-label": t("topbar.menuOpenAria"),
        children: /* @__PURE__ */ jsx(Menu, { size: 18, strokeWidth: 2 })
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "adminer-Topbar__crumbs", children: crumbs.map((c, i) => {
      const isLast = i === crumbs.length - 1;
      return /* @__PURE__ */ jsxs("span", { children: [
        i > 0 && /* @__PURE__ */ jsx("span", { className: "adminer-Topbar__sep", children: " / " }),
        isLast ? /* @__PURE__ */ jsx("strong", { children: c.label }) : c.href ? /* @__PURE__ */ jsx(Link, { to: c.href, children: c.label }) : /* @__PURE__ */ jsx("span", { children: c.label })
      ] }, i);
    }) }),
    right && /* @__PURE__ */ jsx("div", { className: "adminer-Topbar__right", children: right })
  ] });
}
function RequireAuth({
  children,
  check,
  loginPath = "/login",
  fallbackPath = "/"
}) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return null;
  if (!user) {
    const returnTo = location.pathname + location.search + location.hash;
    return /* @__PURE__ */ jsx(
      Navigate,
      {
        to: `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`,
        replace: true
      }
    );
  }
  if (check && !check(user)) {
    return /* @__PURE__ */ jsx(Navigate, { to: fallbackPath, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireGuest({ children, returnTo }) {
  const { user, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  if (isLoading) return null;
  if (user) {
    return /* @__PURE__ */ jsx(Navigate, { to: getSafeReturnTo(searchParams.get("returnTo"), returnTo), replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

export { AppShell, RequireAuth, RequireGuest, ShellContext, Sidebar, Topbar, useShellDrawer };
//# sourceMappingURL=shell.js.map
//# sourceMappingURL=shell.js.map